import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CurrencyInput, formatCurrency } from "@/components/ui/currency-input";
import {
  CreditCard,
  Plus,
  Search,
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Receipt,
  AlertCircle,
  Edit2,
  ArrowDownLeft,
} from "lucide-react";
import { API } from "@/App";
import { format, startOfMonth, endOfMonth, isWithinInterval, parseISO, subMonths } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { parseTurkishNumber } from "@/utils/numberUtils";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

export default function PaymentsModule({ businessId }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "packages"]);
  const { formatPrice, currencySymbol } = useCurrency();

  const [payments, setPayments] = useState([]);
  const [memberPackages, setMemberPackages] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberCards, setMemberCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  
  // Loading states for preventing double-click
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  
  // Date range filter
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  });
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Form state
  const [selectedMemberPackage, setSelectedMemberPackage] = useState("");
  const [selectedPackageDebt, setSelectedPackageDebt] = useState(0);
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });

  // Edit form state
  const [editAmount, setEditAmount] = useState("");
  const [editPaymentMethod, setEditPaymentMethod] = useState("");
  const [editNotes, setEditNotes] = useState("");
  
  // Refund modal state
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundMemberId, setRefundMemberId] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refundMethod, setRefundMethod] = useState("cash");

  // Stats
  const [stats, setStats] = useState({
    totalReceived: 0,
    totalPending: 0,
    thisMonth: 0,
    totalRefunded: 0,
  });

  useEffect(() => {
    if (businessId) {
      fetchData();
      fetchMemberCards();
    }
  }, [businessId]);
  
  const fetchMemberCards = async () => {
    try {
      const response = await fetch(`${API}/public/member-cards`);
      if (response.ok) {
        const data = await response.json();
        setMemberCards(data.member_cards || []);
      }
    } catch (error) {
      console.error("Error fetching member cards:", error);
    }
  };

  const fetchData = async () => {
    try {
      const membersRes = await fetch(`${API}/businesses/${businessId}/members`, {
        credentials: "include",
      });

      if (membersRes.ok) {
        const membersData = await membersRes.json();
        setMembers(membersData);
        
        // Fetch all packages and payments in parallel (no limit)
        const allPackages = [];
        const allPayments = [];
        
        // Batch fetch packages for all members
        const pkgPromises = membersData.map(member => 
          fetch(`${API}/members/${member.member_id}/packages`, { credentials: "include" })
            .then(res => res.ok ? res.json() : [])
            .catch(() => [])
        );
        
        const pkgResults = await Promise.all(pkgPromises);
        
        // Collect all packages and prepare payment fetch
        const paymentPromises = [];
        pkgResults.forEach((pkgs, idx) => {
          const member = membersData[idx];
          pkgs.forEach(pkg => {
            const totalPrice = pkg.sale_price || pkg.price || 0;
            const paidAmount = pkg.paid_amount || 0;
            
            // İptal edilen paketler kontrolü:
            const isCancelled = pkg.status === 'cancelled';
            const isPaidCancel = isCancelled && (pkg.cancel_type === 'paid_cancel' || pkg.keep_debt === true || pkg.debt_action === 'keep_debt');
            const isRefundCancel = isCancelled && pkg.cancel_type === 'refund';
            const isFreeCancel = isCancelled && !isRefundCancel && !isPaidCancel;
            
            let debt = 0;
            if (isRefundCancel || isFreeCancel) {
              // İade ile iptal veya ücretsiz iptal - borç sıfırlanır
              debt = 0;
            } else if (isPaidCancel) {
              // Ücretli iptal - paketin belirlenen borcu kalır
              debt = pkg.debt != null ? pkg.debt : Math.max(0, totalPrice - paidAmount);
            } else {
              // Aktif paketler
              debt = Math.max(0, totalPrice - paidAmount);
            }
            
            allPackages.push({ 
              ...pkg, 
              member,
              price: totalPrice,
              debt: debt,
              isCancelled: isCancelled,
              isPaidCancel: isPaidCancel,
              isFreeCancel: isFreeCancel,
              isRefundCancel: isRefundCancel
            });
            
            // Queue payment fetch
            paymentPromises.push(
              fetch(`${API}/member-packages/${pkg.member_package_id}/payments`, { credentials: "include" })
                .then(res => res.ok ? res.json() : { payments: [] })
                .then(payData => {
                  const pays = Array.isArray(payData) ? payData : (payData.payments || []);
                  return pays.map(pay => ({ 
                    ...pay, 
                    member, 
                    package: pkg,
                    payment_date: pay.payment_date || pay.created_at
                  }));
                })
                .catch(() => [])
            );
          });
        });
        
        // Fetch all payments in parallel
        const payResults = await Promise.all(paymentPromises);
        payResults.forEach(pays => allPayments.push(...pays));
        
        setMemberPackages(allPackages);
        
        // Also fetch all refunds for this business
        try {
          const refundsRes = await fetch(`${API}/businesses/${businessId}/refunds`, {
            credentials: "include",
          });
          if (refundsRes.ok) {
            const refundsData = await refundsRes.json();
            const refunds = Array.isArray(refundsData) ? refundsData : [];
            // Add refunds to payments list - BUT skip if already added from member-packages endpoint
            const existingPaymentIds = new Set(allPayments.map(p => p.payment_id));
            refunds.forEach(refund => {
              // Skip if this refund is already in allPayments (came from member-packages endpoint)
              if (existingPaymentIds.has(refund.payment_id)) {
                return;
              }
              const member = membersData.find(m => m.member_id === refund.member_id);
              allPayments.push({
                ...refund,
                member: member || { name: "Bilinmiyor", surname: "" },
                payment_type: "refund"
              });
            });
          }
        } catch (e) {
          console.error("Error fetching refunds:", e);
        }
        
        // Sort payments by date (newest first)
        allPayments.sort((a, b) => {
          const dateA = new Date(a.payment_date || a.created_at || 0);
          const dateB = new Date(b.payment_date || b.created_at || 0);
          return dateB - dateA;
        });
        
        setPayments(allPayments);
        
        // Calculate stats
        // Toplam tahsilat (sadece pozitif ödemeler)
        const totalReceived = allPayments.reduce((sum, p) => sum + Math.max(0, p.amount || 0), 0);
        // Toplam iade (negatif ödemeler - mutlak değer)
        const totalRefunded = allPayments.reduce((sum, p) => sum + Math.abs(Math.min(0, p.amount || 0)), 0);
        // Bekleyen borç: Aktif paketler + Borcu korunan iptal paketler (paid_cancel / keep_debt)
        const packagesWithDebt = allPackages.filter(p => 
          p.status !== 'deleted' && 
          (p.status !== 'cancelled' || p.cancel_type === 'paid_cancel' || p.keep_debt === true || p.cancel_type === 'free')
        );
        const totalPending = packagesWithDebt.reduce((sum, p) => sum + Math.max(0, p.debt || 0), 0);
        
        // This month's payments (including refunds)
        const now = new Date();
        const thisMonthPayments = allPayments.filter(p => {
          if (!p.payment_date && !p.created_at) return false;
          const payDate = new Date(p.payment_date || p.created_at);
          return payDate.getMonth() === now.getMonth() && payDate.getFullYear() === now.getFullYear();
        });
        // Bu ayki net gelir (tahsilat - iade)
        const thisMonth = thisMonthPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
        
        setStats({
          totalReceived,
          totalPending,
          thisMonth,
          totalRefunded,
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async () => {
    if (!selectedMemberPackage || !amount) {
      toast.error("Paket ve tutar zorunludur");
      return;
    }

    // CurrencyInput already returns a number
    const numAmount = typeof amount === 'number' ? amount : parseTurkishNumber(amount) || 0;
    
    // Validate amount doesn't exceed debt
    if (numAmount > selectedPackageDebt) {
      toast.error(`Kalan borç ${formatCurrency(selectedPackageDebt)}. Bu tutardan fazla ödeme alamazsınız.`);
      return;
    }

    if (numAmount <= 0) {
      toast.error(t("common:toast_tutar0danBuyukOlmalidir"));
      return;
    }
    
    // Prevent double-click
    if (isAddingPayment) return;
    setIsAddingPayment(true);

    try {
      const response = await fetch(`${API}/payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          member_package_id: selectedMemberPackage,
          amount: numAmount,
          payment_method: paymentMethod || null,
          payment_date: paymentDate || null,
          // Paket adını notes'a ekle
          notes: notes || (() => {
            const mp = memberPackages.find(p => p.member_package_id === selectedMemberPackage);
            return mp ? `${t('packages:packageSaleNote')} - ${mp.package_name || mp.package_id}` : null;
          })(),
        }),
      });

      if (response.ok) {
        toast.success(t('payments:paymentSaved'));
        fetchData();
        handleCloseModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsAddingPayment(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMemberPackage("");
    setSelectedPackageDebt(0);
    setAmount("");
    setPaymentMethod("");
    setNotes("");
    const d = new Date();
    setPaymentDate(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
  };
  
  // Handle refund
  const handleRefund = async () => {
    if (!refundMemberId || !refundAmount || !refundReason.trim()) {
      toast.error(t('common:allFieldsRequired'));
      return;
    }
    
    // CurrencyInput already returns a number
    const numAmount = typeof refundAmount === 'number' ? refundAmount : parseTurkishNumber(refundAmount) || 0;
    if (numAmount <= 0) {
      toast.error(t("common:toast_i̇adeTutari0danBuyukOlmalidir"));
      return;
    }
    
    // Prevent double-click
    if (isRefunding) return;
    setIsRefunding(true);
    
    try {
      const response = await fetch(`${API}/payments/refund`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          business_id: businessId,
          member_id: refundMemberId,
          amount: numAmount,
          reason: refundReason,
          payment_method: refundMethod,
        }),
      });
      
      if (response.ok) {
        toast.success(t("common:toast_i̇adeKaydedildi"));
        fetchData();
        setShowRefundModal(false);
        setRefundMemberId("");
        setRefundAmount("");
        setRefundReason("");
        setRefundMethod("cash");
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsRefunding(false);
    }
  };

  // Handle amount change with debt validation
  const handleAmountChange = (value) => {
    // CurrencyInput already passes a number
    const numValue = typeof value === 'number' ? value : parseTurkishNumber(value) || 0;
    
    if (numValue > selectedPackageDebt && selectedPackageDebt > 0) {
      toast.warning(`Kalan borç ${formatCurrency(selectedPackageDebt)}. Bu tutardan fazla ödeme alamazsınız.`);
      setAmount(selectedPackageDebt);
      return;
    }
    
    setAmount(value);
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setEditAmount(payment.amount?.toString() || "");
    setEditPaymentMethod(payment.payment_method || "");
    setEditNotes(payment.notes || "");
    setShowEditModal(true);
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment) return;
    
    // Prevent double-click
    if (isUpdatingPayment) return;
    setIsUpdatingPayment(true);
    
    try {
      const numEditAmount = typeof editAmount === 'number' ? editAmount : parseTurkishNumber(editAmount) || 0;
      const response = await fetch(`${API}/payments/${editingPayment.payment_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          amount: numEditAmount,
          payment_method: editPaymentMethod,
          notes: editNotes,
        }),
      });

      if (response.ok) {
        toast.success(t("common:toast_odemeGuncellendi"));
        setShowEditModal(false);
        setEditingPayment(null);
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || t('settings:updateFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsUpdatingPayment(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingPayment(null);
    setEditAmount("");
    setEditPaymentMethod("");
    setEditNotes("");
  };

  const filteredPayments = payments.filter((p) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        p.member?.name?.toLowerCase().includes(query) ||
        p.member?.surname?.toLowerCase().includes(query)
      );
      if (!matchesSearch) return false;
    }
    
    // Date filter
    if (dateFilter !== "all") {
      const paymentDate = p.payment_date || p.created_at;
      if (!paymentDate) return false;
      
      const date = new Date(paymentDate);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      if (dateFilter === "today") {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (date < today || date >= tomorrow) return false;
      } else if (dateFilter === "week") {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        if (date < weekAgo) return false;
      } else if (dateFilter === "month") {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        if (date < monthStart) return false;
      } else if (dateFilter === "custom" && dateRange.from && dateRange.to) {
        const from = new Date(dateRange.from);
        from.setHours(0, 0, 0, 0);
        const to = new Date(dateRange.to);
        to.setHours(23, 59, 59, 999);
        if (date < from || date > to) return false;
      }
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t('common:payments')}</h2>
          <p className="text-grey text-sm">{t("common:odemeTakibiVeTahsilat")}</p>
        </div>
        
        <div className="flex gap-2">
          {/* İade Et butonu şimdilik gizli
          <Button
            variant="outline"
            className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
            onClick={() => setShowRefundModal(true)}
            data-testid="refund-btn"
          >
            <TrendingDown className="w-4 h-4 mr-2" />
            {t("common:refund")}
          </Button>
          */}

          <Dialog open={showModal} onOpenChange={setShowModal}>
            <DialogTrigger asChild>
              <Button
                className="bg-navy hover:bg-navy/90 rounded-xl"
                data-testid="add-payment-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t("common:addPayment")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t("common:yeniOdemeKaydi")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>{t("packages:memberPackageRequired")}</Label>
                <Select 
                  value={selectedMemberPackage} 
                  onValueChange={(v) => {
                    setSelectedMemberPackage(v);
                    // Auto-fill remaining amount and set debt limit
                    const mp = memberPackages.find(p => p.member_package_id === v);
                    if (mp) {
                      const remaining = mp.debt || 0;
                      setSelectedPackageDebt(remaining);
                      if (remaining > 0) {
                        setAmount(remaining.toString());
                      }
                    }
                  }}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={t("packages:ph_paketSecin")} />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Only show packages with remaining debt */}
                    {memberPackages.filter(mp => (mp.debt || 0) > 0).length === 0 ? (
                      <div className="p-3 text-center text-grey text-sm">
                        {t("packages:noDebtPackage")}
                      </div>
                    ) : (
                      memberPackages
                        .filter(mp => (mp.debt || 0) > 0)
                        .map((mp) => (
                          <SelectItem key={mp.member_package_id} value={mp.member_package_id}>
                            <div className="flex items-center gap-2">
                              <span>{mp.member?.name} {mp.member?.surname} - {mp.package_name}</span>
                              <Badge variant="destructive" className="text-xs">
                                {formatPrice((mp.debt || 0))} {t("packages:remainingLeft")}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Seçilen paket bilgisi */}
              {selectedMemberPackage && (() => {
                const mp = memberPackages.find(p => p.member_package_id === selectedMemberPackage);
                if (!mp) return null;
                const total = mp.price || 0;
                const paid = mp.paid_amount || 0;
                const remaining = mp.debt || 0;
                
                return (
                  <div className="p-3 bg-slate-50 rounded-xl text-sm space-y-1">
                    <p><span className="text-grey">{t('packages:packageTotal')}:</span> <span className="font-semibold">{formatPrice(total)}</span></p>
                    <p><span className="text-grey">{t('common:paidAmount3')}:</span> <span className="font-semibold text-green-600">{formatPrice(paid)}</span></p>
                    <p><span className="text-grey">{t('packages:remainingAmount2')}:</span> <span className={`font-semibold ${remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>{formatPrice(remaining)}</span></p>
                  </div>
                );
              })()}

              <div>
                <Label>{t("common:amountRequired")}</Label>
                <CurrencyInput
                  value={amount}
                  onChange={(val) => handleAmountChange(val)}
                  placeholder="0"
                  className="rounded-xl"
                  data-testid="payment-amount-input"
                />
                {selectedPackageDebt > 0 && (typeof amount === 'number' ? amount : parseTurkishNumber(amount) || 0) > selectedPackageDebt && (
                  <p className="text-xs text-red-500 mt-1">
                    {t("packages:cannotExceedDebt")}
                  </p>
                )}
              </div>

              <div>
                <Label>Tahsilat Tarihi</Label>
                <DatePickerTR
                  value={paymentDate}
                  onChange={(ds) => ds && setPaymentDate(ds)}
                  returnDateString
                  placeholder="Tarih seç"
                  data-testid="payments-collect-date"
                />
              </div>

              <div>
                <Label>{t('payments:paymentMethod')}</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={t("common:ph_secin")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                    <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                    <SelectItem value="bank_transfer">{t('payments:bankTransfer')}</SelectItem>
                    <SelectItem value="eft">{t('payments:eft')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t("common:notesLabel")}</Label>
                <Textarea
                  placeholder={t("common:ph_odemeIleIlgiliNotlar")}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="rounded-xl"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={handleCloseModal}
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                  onClick={handleAddPayment}
                  loading={isAddingPayment}
                  loadingText="Kaydediliyor..."
                  data-testid="save-payment-btn"
                >
                  {t('common:save')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-grey text-sm">{t('common:totalCollection')}</p>
                <p className="font-heading font-bold text-2xl text-navy">
                  {formatPrice(stats.totalReceived)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-grey text-sm">{t("common:bekleyenOdeme")}</p>
                <p className="font-heading font-bold text-2xl text-navy">
                  {formatPrice(stats.totalPending)}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-grey text-sm">{t('common:thisMonth')}</p>
                <p className="font-heading font-bold text-2xl text-navy">
                  {formatPrice(stats.thisMonth)}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-grey text-sm">{t("common:toplamİade")}</p>
                <p className="font-heading font-bold text-2xl text-red-600">
                  {formatPrice(stats.totalRefunded)}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                <ArrowDownLeft className="w-6 h-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* {t("common:pendingDebts")} Listesi */}
      {memberPackages.filter(mp => (mp.debt || 0) > 0).length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-orange-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-orange-600 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {t("common:pendingDebts")} ({memberPackages.filter(mp => (mp.debt || 0) > 0).length} paket)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {/* Desktop Table */}
            <div className="overflow-x-auto max-h-[300px] hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common:member")}</TableHead>
                    <TableHead>Paket No</TableHead>
                    <TableHead>{t("packages:package")}</TableHead>
                    <TableHead>{t('common:status')}</TableHead>
                    <TableHead className="text-right">{t('payments:salePrice')}</TableHead>
                    <TableHead className="text-right">{t("common:paidAmount")}</TableHead>
                    <TableHead className="text-right">{t("packages:remainingDebt")}</TableHead>
                    <TableHead className="text-center">{t("common:i̇slem")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {memberPackages
                    .filter(mp => (mp.debt || 0) > 0)
                    .sort((a, b) => (b.debt || 0) - (a.debt || 0))
                    .map((mp) => {
                      const member = members.find(m => m.member_id === mp.member_id);
                      const isCancelled = mp.status === 'cancelled';
                      const isRefundCancel = isCancelled && mp.cancel_type === 'refund';
                      const isPaidCancel = isCancelled && (mp.cancel_type === 'paid_cancel' || mp.keep_debt === true);
                      const isFreeCancel = isCancelled && !isRefundCancel && !isPaidCancel;
                      return (
                        <TableRow key={mp.member_package_id || mp.mp_id}>
                          <TableCell className="font-medium">
                            {member?.name} {member?.surname}
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-xs text-navy" data-testid={`payment-package-no-${mp.mp_id || mp.member_package_id}`}>
                              {mp.package_no || "-"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={isCancelled ? 'line-through text-grey' : ''}>
                              {mp.package_name}
                            </span>
                          </TableCell>
                          <TableCell>
                            {isRefundCancel ? (
                              <Badge className="bg-red-100 text-red-700 text-xs">İadeli İptal</Badge>
                            ) : isPaidCancel ? (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Ücretli İptal</Badge>
                            ) : isFreeCancel ? (
                              <Badge className="bg-orange-100 text-orange-700 text-xs">{t('members:freeCancellation')}</Badge>
                            ) : isCancelled ? (
                              <Badge variant="secondary" className="text-xs">{t('common:cancel')}</Badge>
                            ) : mp.status === 'active' ? (
                              <Badge className="bg-green-100 text-green-700 text-xs">{t('admin:active')}</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">{mp.status}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatPrice((mp.price || mp.sale_price || 0))}
                          </TableCell>
                          <TableCell className="text-right text-green-600">
                            {formatPrice((mp.paid_amount || 0))}
                          </TableCell>
                          <TableCell className="text-right font-bold text-red-600">
                            {formatPrice((mp.debt || 0))}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs"
                              onClick={() => {
                                setSelectedMemberPackage(mp.member_package_id || mp.mp_id);
                                setSelectedPackageDebt(mp.debt || 0);
                                setAmount((mp.debt || 0).toString());
                                setShowModal(true);
                              }}
                              data-testid={`pay-debt-${mp.member_package_id || mp.mp_id}`}
                            >
                              <DollarSign className="w-3 h-3 mr-1" />
                              {t('common:collectPayment')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
            {/* Mobile Cards */}
            <div className="md:hidden space-y-2 max-h-[300px] overflow-y-auto">
              {memberPackages
                .filter(mp => (mp.debt || 0) > 0)
                .sort((a, b) => (b.debt || 0) - (a.debt || 0))
                .map((mp) => {
                  const member = members.find(m => m.member_id === mp.member_id);
                  const isCancelled = mp.status === 'cancelled';
                  return (
                    <div key={mp.member_package_id || mp.mp_id} className="bg-slate-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-navy truncate">{member?.name} {member?.surname}</p>
                          <p className={`text-xs text-grey ${isCancelled ? 'line-through' : ''}`}>{mp.package_name}</p>
                        </div>
                        <span className="font-bold text-red-600">{formatPrice((mp.debt || 0))}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-grey">{t('common:paidAmount3')}: {formatPrice((mp.paid_amount || 0))}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => {
                            setSelectedMemberPackage(mp.member_package_id || mp.mp_id);
                            setSelectedPackageDebt(mp.debt || 0);
                            setAmount((mp.debt || 0).toString());
                            setShowModal(true);
                          }}
                        >
                          <DollarSign className="w-3 h-3 mr-1" />
                          {t('common:collectPayment')}
                        </Button>
                      </div>
                    </div>
                  );
                })}
            </div>
            <div className="mt-3 pt-3 border-t flex justify-between items-center">
              <span className="text-grey">{t('common:totalPending')}</span>
              <span className="font-bold text-lg text-red-600">
                {formatPrice(memberPackages.filter(mp => (mp.debt || 0) > 0).reduce((sum, mp) => sum + (mp.debt || 0), 0))}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey" />
              <Input
                placeholder={t('members:searchMember')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-xl"
                data-testid="search-payments"
              />
            </div>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-40 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common:all')}</SelectItem>
                <SelectItem value="today">{t('common:today')}</SelectItem>
                <SelectItem value="week">{t('common:thisWeek')}</SelectItem>
                <SelectItem value="month">{t('common:thisMonth')}</SelectItem>
                <SelectItem value="custom">{t("common:tarihAraligi")}</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Tarih Aralığı Seçici */}
            {dateFilter === "custom" && (
              <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="rounded-xl min-w-[200px] justify-start text-left">
                    <Calendar className="w-4 h-4 mr-2" />
                    {dateRange.from && dateRange.to ? (
                      <>
                        {format(dateRange.from, "dd/MM/yyyy")} - {format(dateRange.to, "dd/MM/yyyy")}
                      </>
                    ) : (
                      t("common:selectDate")
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs mb-1 block">{t('settings:startTime')}</Label>
                        <CalendarComponent
                          mode="single"
                          selected={dateRange.from}
                          onSelect={(date) => setDateRange(prev => ({ ...prev, from: date }))}
                          locale={tr}
                          className="rounded-md border"
                        />
                      </div>
                      <div>
                        <Label className="text-xs mb-1 block">{t('settings:endTime')}</Label>
                        <CalendarComponent
                          mode="single"
                          selected={dateRange.to}
                          onSelect={(date) => setDateRange(prev => ({ ...prev, to: date }))}
                          locale={tr}
                          className="rounded-md border"
                        />
                      </div>
                    </div>
                    <Button 
                      className="w-full rounded-xl" 
                      onClick={() => setShowDatePicker(false)}
                    >
                      Uygula
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payments List */}
      {filteredPayments.length > 0 ? (
        <Card className="border-0 shadow-sm">
          {/* Desktop Table */}
          <div className="overflow-x-auto hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">{t("common:member")}</TableHead>
                  <TableHead className="whitespace-nowrap">Paket No</TableHead>
                  <TableHead className="whitespace-nowrap">{t("packages:package")}</TableHead>
                  <TableHead className="whitespace-nowrap">{t("common:branch")}</TableHead>
                  <TableHead className="whitespace-nowrap">{t('common:amount')}</TableHead>
                  <TableHead className="whitespace-nowrap">{t("common:method")}</TableHead>
                  <TableHead className="whitespace-nowrap">{t('common:date')}</TableHead>
                  <TableHead className="whitespace-nowrap">{t("common:notesLabel")}</TableHead>
                  <TableHead className="text-right whitespace-nowrap">{t('common:actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayments.map((payment) => (
                  <TableRow
                    key={payment.payment_id}
                    data-testid={`payment-row-${payment.payment_id}`}
                  >
                    <TableCell className="whitespace-nowrap">
                      <div>
                        <p className="font-medium">
                          {payment.member?.name} {payment.member?.surname}
                        </p>
                        <p className="text-xs text-grey">
                          {payment.package?.remaining_sessions} {t("packages:sessionsLeft")}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <span className="text-sm font-mono text-grey">{payment.package?.package_no || "-"}</span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {payment.package?.package_name ? (
                        <Badge variant="secondary" className="font-medium">{payment.package.package_name}</Badge>
                      ) : "-"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {payment.package?.branch ? (
                        <Badge variant="outline">{payment.package.branch}</Badge>
                      ) : "-"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <span className={`font-semibold ${payment.amount < 0 ? "text-red-600" : "text-green-600"}`}>
                        {payment.amount < 0 ? "" : "+"}{payment.amount < 0 ? "-" : ""}{formatPrice(Math.abs(payment.amount))}
                      </span>
                      {payment.payment_type === "refund" && (
                        <span className="text-xs text-red-500 block">{t("common:refund")}</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <Badge variant="secondary">
                        {payment.payment_method === "cash" && t('payments:cash')}
                        {payment.payment_method === "credit_card" && t('payments:creditCard')}
                        {payment.payment_method === "bank_transfer" && "Havale/EFT"}
                        {payment.payment_method === "multisport" && "Multisport"}
                        {!payment.payment_method && "Belirtilmedi"}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {payment.payment_date
                        ? format(new Date(payment.payment_date), "dd/MM/yyyy HH:mm", {
                            locale: getDateLocale(),
                          })
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <span className="text-grey text-sm truncate max-w-xs block">
                        {payment.notes || "-"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditPayment(payment)}
                        data-testid={`edit-payment-${payment.payment_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {/* Mobile Cards */}
          <div className="md:hidden p-3 space-y-3">
            {filteredPayments.map((payment) => (
              <div
                key={payment.payment_id}
                className="bg-slate-50 rounded-lg p-3"
                data-testid={`payment-mobile-${payment.payment_id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-navy truncate">
                      {payment.member?.name} {payment.member?.surname}
                    </p>
                    {payment.package?.package_name && (
                      <p className="text-xs text-blue-600 truncate mt-0.5">
                        📦 {payment.package.package_name}
                        {payment.package?.package_no ? ` • ${payment.package.package_no}` : ""}
                        {payment.package?.branch ? ` • ${payment.package.branch}` : ""}
                      </p>
                    )}
                    <p className="text-xs text-grey">
                      {payment.payment_date
                        ? format(new Date(payment.payment_date), "dd/MM/yyyy HH:mm", { locale: getDateLocale() })
                        : "-"}
                    </p>
                  </div>
                  <span className={`font-bold ${payment.amount < 0 ? "text-red-600" : "text-green-600"}`}>
                    {payment.amount < 0 ? "-" : "+"}{formatPrice(Math.abs(payment.amount))}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {payment.payment_method === "cash" && t('payments:cash')}
                      {payment.payment_method === "credit_card" && t("admin:creditCardShort")}
                      {payment.payment_method === "bank_transfer" && "Havale"}
                      {payment.payment_method === "multisport" && "Multisport"}
                      {!payment.payment_method && "-"}
                    </Badge>
                    {payment.payment_type === "refund" && (
                      <Badge className="bg-red-100 text-red-700 text-xs">{t("common:refund")}</Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => handleEditPayment(payment)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <CreditCard className="w-12 h-12 text-grey mx-auto mb-3" />
            <p className="text-grey">
              {searchQuery ? t("common:paymentNotFound") : t("common:noPaymentRecord")}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Edit Payment Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("common:odemeDuzenle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {editingPayment && (
              <div className="bg-gray-50 p-3 rounded-xl text-sm">
                <p className="font-medium">{editingPayment.member?.name} {editingPayment.member?.surname}</p>
                <p className="text-grey">{editingPayment.package?.package_name}</p>
              </div>
            )}
            
            <div>
              <Label>Tutar ({currencySymbol}) *</Label>
              <Input
                type="number"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                placeholder="0"
                className="rounded-xl"
                data-testid="edit-payment-amount"
              />
            </div>

            <div>
              <Label>{t('payments:paymentMethod')}</Label>
              <Select value={editPaymentMethod} onValueChange={setEditPaymentMethod}>
                <SelectTrigger className="rounded-xl" data-testid="edit-payment-method">
                  <SelectValue placeholder={t("common:ph_secin")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                  <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                  <SelectItem value="bank_transfer">{t('payments:bankTransfer')}</SelectItem>
                  <SelectItem value="eft">{t('payments:eft')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t("common:notesLabel")}</Label>
              <Textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder={t("common:ph_odemeIleIlgiliNot")}
                className="rounded-xl"
                data-testid="edit-payment-notes"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={handleCloseEditModal}
                className="flex-1 rounded-xl"
              >
                {t("common:cancel")}
              </Button>
              <Button
                onClick={handleUpdatePayment}
                disabled={!editAmount || isUpdatingPayment}
                className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                data-testid="save-edit-payment-btn"
              >
                {isUpdatingPayment ? t("common:saving") : t('common:save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Refund Modal */}
      <Dialog open={showRefundModal} onOpenChange={setShowRefundModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">{t("common:paraİadesi")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>{t("admin:memberRequired2")}</Label>
              <Select value={refundMemberId} onValueChange={setRefundMemberId}>
                <SelectTrigger className="rounded-xl" data-testid="refund-member-select">
                  <SelectValue placeholder={t("common:ph_uyeSecin")} />
                </SelectTrigger>
                <SelectContent>
                  {members.map((member) => (
                    <SelectItem key={member.member_id} value={member.member_id}>
                      {member.name} {member.surname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{t("common:refundAmountRequired")}</Label>
              <Input
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder="0"
                className="rounded-xl"
                min={0}
                data-testid="refund-amount-input"
              />
            </div>
            
            <div>
              <Label>{t("common:i̇adeYontemi")}</Label>
              <Select value={refundMethod} onValueChange={setRefundMethod}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                  <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                  <SelectItem value="bank_transfer">{t("payments:bankTransfer")}</SelectItem>
                  <SelectItem value="eft">{t("payments:eft")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{t("common:refundReasonRequired")}</Label>
              <Textarea
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder={t("common:ph_i̇adeNedeniniAciklayin")}
                className="rounded-xl"
                data-testid="refund-reason-input"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowRefundModal(false)}
                className="flex-1 rounded-xl"
              >
                {t("common:giveUp2")}
              </Button>
              <Button
                onClick={handleRefund}
                disabled={!refundMemberId || !refundAmount || !refundReason.trim() || isRefunding}
                className="flex-1 bg-red-600 hover:bg-red-700 rounded-xl"
                data-testid="confirm-refund-btn"
              >
                {isRefunding ? t("common:refunding") : t("common:refund")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
